(() => {
  if (window.ScrptdProductCardTools) return;

  const CARD_SELECTOR = [
    '.collection-item.w-dyn-item',
    '.minimal-product-row__item',
    '.collection-display-row__item',
    '.product-recommendations-card',
    '.product-item'
  ].join(',');
  const RONDELL_SELECTOR = '.scrptd-rondell__item';
  const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const productCache = new Map();
  let modal;
  let lastTrigger;
  let pickerMode = 'cart';
  let pickerProduct = null;
  let pickerSelections = new Map();
  let pickerOptionIndexes = [];
  let cardObserver;
  let mutationFrame = 0;
  const pendingMutationRoots = new Set();
  let activePreloads = 0;
  const preloadQueue = [];
  const queuedPreloads = new WeakSet();
  const MAX_ACTIVE_PRELOADS = 2;

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const normalizeSize = (value) => String(value || '').trim().toUpperCase();
  const productHandleFromUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return decodeURIComponent(url.pathname.match(/\/products\/([^/?#]+)/)?.[1] || '');
    } catch (_) {
      return '';
    }
  };
  const productUrlFromHandle = (handle) => `${rootPath()}products/${encodeURIComponent(handle)}`;
  const imageUrl = (image) => {
    const source = typeof image === 'string' ? image : image?.src;
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      if (url.hostname.includes('cdn.shopify.com')) url.searchParams.set('width', '900');
      return url.toString();
    } catch (_) {
      return source;
    }
  };
  const loadProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(`${productUrlFromHandle(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Produktdaten konnten nicht geladen werden.');
        return data;
      }).catch((error) => {
        productCache.delete(handle);
        throw error;
      }));
    }
    return productCache.get(handle);
  };

  const pumpPreloads = () => {
    while (activePreloads < MAX_ACTIVE_PRELOADS && preloadQueue.length) {
      const job = preloadQueue.shift();
      activePreloads += 1;
      Promise.resolve()
        .then(job)
        .catch(() => {})
        .finally(() => {
          activePreloads -= 1;
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(pumpPreloads, { timeout: 400 });
          } else {
            window.setTimeout(pumpPreloads, 0);
          }
        });
    }
  };

  const queuePreload = (card, job) => {
    if (queuedPreloads.has(card)) return;
    queuedPreloads.add(card);
    preloadQueue.push(job);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(pumpPreloads, { timeout: 650 });
    } else {
      window.setTimeout(pumpPreloads, 0);
    }
  };

  const buildModal = () => {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'scrptd-quick-add-modal';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="scrptd-quick-add-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="ScrptdQuickAddTitle">
        <button class="scrptd-quick-add-modal__close" type="button" aria-label="Schließen" data-quick-add-close>&times;</button>
        <p class="scrptd-quick-add-modal__eyebrow" data-quick-add-eyebrow>GRÖSSE AUSWÄHLEN</p>
        <h2 class="scrptd-quick-add-modal__title" id="ScrptdQuickAddTitle" data-quick-add-title></h2>
        <div class="scrptd-quick-add-modal__sizes" data-quick-add-sizes></div>
        <button class="scrptd-quick-add-modal__confirm" type="button" data-quick-add-confirm disabled>IN DEN WARENKORB</button>
        <p class="scrptd-quick-add-modal__status" data-quick-add-status aria-live="polite"></p>
      </div>`;
    document.body.append(modal);

    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target.closest('[data-quick-add-close]')) closeModal();
      const choice = event.target.closest('[data-quick-add-choice]');
      if (choice && !choice.disabled) selectPickerChoice(choice);
      const confirm = event.target.closest('[data-quick-add-confirm]');
      if (confirm && !confirm.disabled && confirm.dataset.quickAddVariant) addVariant(confirm);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) closeModal();
    });
    return modal;
  };

  const closeModal = () => {
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('scrptd-quick-add-open');
    lastTrigger?.focus();
  };

  const openCart = (cart) => {
    if (cart && typeof window.openCartDrawerWithCart === 'function') {
      window.openCartDrawerWithCart(cart);
      return;
    }
    if (typeof window.openCartDrawer === 'function') {
      window.openCartDrawer();
      return;
    }
    const drawer = document.getElementById('cart-drawer');
    const overlay = document.getElementById('cart-drawer-overlay');
    if (drawer) {
      drawer.classList.add('open');
      overlay?.classList.add('active');
      return;
    }
    window.location.assign(`${rootPath()}cart`);
  };

  const addVariant = async (button) => {
    const status = modal.querySelector('[data-quick-add-status]');
    modal.querySelectorAll('[data-quick-add-choice], [data-quick-add-confirm]').forEach((item) => { item.disabled = true; });
    button.setAttribute('aria-busy', 'true');
    status.textContent = 'WIRD HINZUGEFÜGT ...';
    try {
      if (pickerMode === 'wishlist') {
        const added = window.ScrptdWishlist?.addProduct?.(pickerProduct, Number(button.dataset.quickAddVariant));
        if (!added) throw new Error('Der Artikel konnte nicht zur Wishlist hinzugefügt werden.');
        status.textContent = 'ZUR WISHLIST HINZUGEFÜGT.';
        closeModal();
        return;
      }
      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.quickAddVariant), quantity: 1 }] })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Der Artikel konnte nicht hinzugefügt werden.');
      const expectedLine = {
        id: Number(button.dataset.quickAddVariant),
        quantity: Math.max(1, Number(result.items?.[0]?.quantity || result.quantity) || 1)
      };
      const cart = typeof window.ScrptdFetchConfirmedCart === 'function'
        ? await window.ScrptdFetchConfirmedCart([expectedLine])
        : await fetch(`${rootPath()}cart.js?_=${Date.now()}`, {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
          }).then((cartResponse) => cartResponse.json());
      closeModal();
      openCart(cart);
    } catch (error) {
      status.textContent = error.message || 'Der Artikel konnte nicht hinzugefügt werden.';
      syncPickerAvailability();
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  const productOptionName = (option, index) => String(typeof option === 'string' ? option : option?.name || `GRÖSSE ${index + 1}`).trim();
  const variantOptions = (variant) => variant.options || [variant.option1, variant.option2, variant.option3];
  const sizeOptionIndexes = (product) => {
    const options = product.options || [];
    const explicit = options.map((option, index) => (/size|gr(?:o|ö|oe)(?:sse|ße)/i.test(productOptionName(option, index)) ? index : -1)).filter((index) => index >= 0);
    if (explicit.length) return explicit;
    return options.map((option, index) => {
      const values = Array.from(new Set((product.variants || []).map((variant) => normalizeSize(variantOptions(variant)[index])).filter(Boolean)));
      return values.length && values.every((value) => SIZE_ORDER.includes(value)) ? index : -1;
    }).filter((index) => index >= 0);
  };

  const sortedOptionValues = (product, index) => {
    const values = Array.from(new Set((product.variants || []).map((variant) => normalizeSize(variantOptions(variant)[index])).filter(Boolean)));
    return SIZE_ORDER.filter((size) => values.includes(size)).concat(values.filter((value) => !SIZE_ORDER.includes(value)));
  };

  const variantMatchesSelections = (variant, selections = pickerSelections) => pickerOptionIndexes.every((index) => {
    const selected = selections.get(index);
    return !selected || normalizeSize(variantOptions(variant)[index]) === selected;
  });

  const syncPickerAvailability = () => {
    if (!modal || !pickerProduct) return;
    const variants = pickerProduct.variants || [];
    modal.querySelectorAll('[data-quick-add-choice]').forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const candidate = new Map(pickerSelections);
      candidate.set(index, button.dataset.optionValue);
      pickerOptionIndexes.forEach((optionIndex) => { if (optionIndex > index) candidate.delete(optionIndex); });
      const available = variants.some((variant) => variant.available && variantMatchesSelections(variant, candidate));
      const selected = pickerSelections.get(index) === button.dataset.optionValue;
      button.disabled = !available;
      button.dataset.available = String(available);
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    const selectedAll = pickerOptionIndexes.every((index) => pickerSelections.has(index));
    const variant = selectedAll ? variants.find((item) => item.available && variantMatchesSelections(item)) : null;
    const confirm = modal.querySelector('[data-quick-add-confirm]');
    confirm.disabled = !variant;
    confirm.textContent = pickerMode === 'wishlist' ? 'ZUR WISHLIST HINZUFÜGEN' : 'IN DEN WARENKORB';
    if (variant) confirm.dataset.quickAddVariant = variant.id;
    else delete confirm.dataset.quickAddVariant;
  };

  const selectPickerChoice = (button) => {
    const index = Number(button.dataset.optionIndex);
    pickerSelections.set(index, button.dataset.optionValue);
    pickerOptionIndexes.forEach((optionIndex) => { if (optionIndex > index) pickerSelections.delete(optionIndex); });
    syncPickerAvailability();
  };

  const openProductPicker = (product, trigger, mode = 'cart') => {
    buildModal();
    lastTrigger = trigger;
    pickerMode = mode;
    pickerProduct = product;
    pickerSelections = new Map();
    pickerOptionIndexes = sizeOptionIndexes(product);
    modal.querySelector('[data-quick-add-status]').textContent = '';
    modal.querySelector('[data-quick-add-eyebrow]').textContent = mode === 'wishlist' ? 'GRÖSSE FÜR WISHLIST WÄHLEN' : 'GRÖSSE AUSWÄHLEN';
    modal.querySelector('[data-quick-add-title]').textContent = product.title || 'PRODUKT';
    const sizeContainer = modal.querySelector('[data-quick-add-sizes]');
    const confirm = modal.querySelector('[data-quick-add-confirm]');
    sizeContainer.replaceChildren();
    confirm.disabled = true;
    confirm.textContent = mode === 'wishlist' ? 'ZUR WISHLIST HINZUFÜGEN' : 'IN DEN WARENKORB';
    delete confirm.dataset.quickAddVariant;

    pickerOptionIndexes.forEach((index) => {
      const group = document.createElement('fieldset');
      group.className = 'scrptd-quick-add-modal__group';
      const legend = document.createElement('legend');
      legend.className = 'scrptd-quick-add-modal__group-title';
      legend.textContent = productOptionName(product.options?.[index], index).toUpperCase();
      const choices = document.createElement('div');
      choices.className = 'scrptd-quick-add-modal__choices';
      sortedOptionValues(product, index).forEach((size) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'scrptd-quick-add-modal__size';
        button.textContent = size;
        button.dataset.quickAddChoice = '';
        button.dataset.optionIndex = String(index);
        button.dataset.optionValue = size;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', `${legend.textContent}: ${size}`);
        choices.append(button);
      });
      group.append(legend, choices);
      sizeContainer.append(group);
    });

    if (!pickerOptionIndexes.length) {
      const availableVariant = (product.variants || []).find((variant) => variant.available);
      const note = document.createElement('p');
      note.className = 'scrptd-quick-add-modal__one-size';
      note.textContent = availableVariant ? 'KEINE GRÖSSENAUSWAHL ERFORDERLICH' : 'DIESES PRODUKT IST AKTUELL AUSVERKAUFT.';
      sizeContainer.append(note);
      confirm.disabled = !availableVariant;
      if (availableVariant) confirm.dataset.quickAddVariant = availableVariant.id;
    } else {
      syncPickerAvailability();
    }

    modal.hidden = false;
    document.body.classList.add('scrptd-quick-add-open');
    modal.querySelector('[data-quick-add-choice]:not(:disabled), [data-quick-add-confirm]:not(:disabled), [data-quick-add-close]')?.focus();
  };

  const installActivationGuard = (element) => {
    if (!element || element.dataset.scrptdActivationGuard === 'true') return;
    element.dataset.scrptdActivationGuard = 'true';
    let startX = 0;
    let startY = 0;
    let moved = false;
    let blockUntil = 0;
    element.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      moved = false;
    }, { passive: true });
    element.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1) return;
      const distanceX = Math.abs(event.touches[0].clientX - startX);
      const distanceY = Math.abs(event.touches[0].clientY - startY);
      if (distanceY > 9 || distanceX > 13) moved = true;
    }, { passive: true });
    element.addEventListener('touchend', () => {
      if (moved) blockUntil = Date.now() + 520;
    }, { passive: true });
    element.addEventListener('click', (event) => {
      if (Date.now() >= blockUntil) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
  };

  const installDragProtection = (link, track) => {
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let dragging = false;
    let moved = false;
    let axis = '';
    let blockClickUntil = 0;

    const snapToNearestSlide = () => {
      const width = Math.max(1, track.clientWidth);
      const index = Math.round(track.scrollLeft / width);
      track.scrollTo({ left: index * width, behavior: 'smooth' });
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      startX = event.clientX;
      startY = event.clientY;
      startLeft = track.scrollLeft;
      dragging = true;
      moved = false;
      axis = '';
    });
    track.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') return;
      if (!dragging) return;
      const distanceX = event.clientX - startX;
      const distanceY = event.clientY - startY;
      if (!axis && Math.max(Math.abs(distanceX), Math.abs(distanceY)) > 7) {
        axis = Math.abs(distanceX) > Math.abs(distanceY) ? 'x' : 'y';
        if (axis === 'x') track.setPointerCapture?.(event.pointerId);
      }
      if (axis !== 'x') return;
      moved = true;
      track.scrollLeft = startLeft - distanceX;
      event.preventDefault();
    });
    const finish = () => {
      if (moved) blockClickUntil = Date.now() + 350;
      dragging = false;
      if (moved) snapToNearestSlide();
    };
    track.addEventListener('pointerup', finish);
    track.addEventListener('pointercancel', finish);

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartLeft = 0;
    let touchAxis = '';
    let touchMoved = false;
    track.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartLeft = track.scrollLeft;
      touchAxis = '';
      touchMoved = false;
    }, { passive: true });
    track.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      const distanceX = touch.clientX - touchStartX;
      const distanceY = touch.clientY - touchStartY;
      if (!touchAxis && Math.max(Math.abs(distanceX), Math.abs(distanceY)) > 6) {
        touchAxis = Math.abs(distanceX) > Math.abs(distanceY) * 1.18 ? 'x' : 'y';
      }
      if (touchAxis !== 'x') return;
      touchMoved = true;
      track.scrollLeft = touchStartLeft - distanceX;
      event.preventDefault();
    }, { passive: false });
    const finishTouch = () => {
      if (!touchMoved) return;
      blockClickUntil = Date.now() + 450;
      moved = false;
      snapToNearestSlide();
    };
    track.addEventListener('touchend', finishTouch, { passive: true });
    track.addEventListener('touchcancel', finishTouch, { passive: true });
    link.addEventListener('click', (event) => {
      if (Date.now() < blockClickUntil || moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);
  };

  const renderCarousel = (link, product) => {
    if (link.querySelector('.scrptd-card-carousel')) return;
    const images = (product.images || []).map(imageUrl).filter(Boolean);
    if (!images.length) return;
    const carousel = document.createElement('div');
    carousel.className = 'scrptd-card-carousel is-preparing';
    const track = document.createElement('div');
    track.className = 'scrptd-card-carousel__track';
    track.setAttribute('aria-label', `Produktbilder von ${product.title}`);
    const progress = document.createElement('div');
    progress.className = 'scrptd-card-carousel__progress';
    progress.style.setProperty('--scrptd-carousel-offset', '0%');
    const progressBar = document.createElement('span');
    progressBar.className = 'scrptd-card-carousel__progress-bar';
    progress.append(progressBar);
    const prioritizeFirstImage = link.getBoundingClientRect().top < window.innerHeight * 1.25;

    images.forEach((src, index) => {
      const slide = document.createElement('div');
      slide.className = 'scrptd-card-carousel__slide';
      const image = document.createElement('img');
      image.src = src;
      image.alt = index === 0 ? product.title : `${product.title}, Ansicht ${index + 1}`;
      image.loading = index === 0 && prioritizeFirstImage ? 'eager' : 'lazy';
      image.decoding = 'async';
      slide.append(image);
      track.append(slide);
    });
    carousel.append(track);
    if (images.length > 1) carousel.append(progress);
    link.append(carousel);
    installDragProtection(link, track);

    const revealCarousel = () => {
      if (!carousel.isConnected || carousel.classList.contains('is-ready')) return;
      requestAnimationFrame(() => {
        carousel.classList.remove('is-preparing');
        carousel.classList.add('is-ready');
        const card = link.closest('.scrptd-product-card');
        const firstCard = document.querySelector('.template-collection .scrptd-product-card');
        const canHint = images.length > 1
          && card === firstCard
          && window.matchMedia('(max-width: 749px)').matches
          && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
          && !document.documentElement.dataset.scrptdFirstSwipeHint;
        if (canHint) {
          document.documentElement.dataset.scrptdFirstSwipeHint = 'true';
          window.setTimeout(() => track.classList.add('is-first-swipe-hint'), 420);
          track.addEventListener('animationend', () => track.classList.remove('is-first-swipe-hint'), { once: true });
        }
      });
    };
    const firstImage = track.querySelector('img');
    if (firstImage?.complete && firstImage.naturalWidth) revealCarousel();
    else {
      firstImage?.addEventListener('load', revealCarousel, { once: true });
      firstImage?.addEventListener('error', revealCarousel, { once: true });
    }

    let frame = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const active = Math.max(0, Math.min(images.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
        const progressRatio = images.length > 1 ? active / (images.length - 1) : 0;
        progress.style.setProperty('--scrptd-carousel-offset', `${progressRatio * 809.091}%`);
      });
    }, { passive: true });
  };

  const prepareMediaLink = (card, link) => {
    const recommendation = card.classList.contains('product-recommendations-card');
    const searchResult = card.classList.contains('product-item');
    if (!recommendation && !searchResult) return link;
    const media = searchResult ? link.querySelector('.product-image-wrapper') : link.querySelector('img');
    if (!media) return link;
    const mediaLink = document.createElement('a');
    mediaLink.href = link.href;
    mediaLink.className = recommendation ? 'scrptd-recommendation-media-link' : 'scrptd-search-media-link';
    media.before(mediaLink);
    mediaLink.append(media);
    link.before(mediaLink);
    return mediaLink;
  };

  const wishlistIcon = () => {
    const template = document.querySelector('[data-scrptd-wishlist-trigger-template]');
    const configured = template?.content?.querySelector('.scrptd-wishlist-trigger__icon')?.cloneNode(true);
    if (configured) {
      configured.classList.add('scrptd-card-wishlist-icon');
      return configured;
    }
    const fallback = document.createElement('span');
    fallback.className = 'scrptd-card-wishlist-icon';
    fallback.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20.8 5.9c-1.8-2-5-2.1-6.9-.2L12 7.5l-1.9-1.8C8.2 3.8 5 3.9 3.2 5.9c-1.8 2-1.6 5.1.3 7l8.5 8.2 8.5-8.2c1.9-1.9 2.1-5 .3-7Z"></path></svg>';
    return fallback;
  };

  const syncWishlistButtons = (scope = document) => {
    const api = window.ScrptdWishlist;
    scope.querySelectorAll?.('.scrptd-card-quick-wishlist').forEach((button) => {
      const active = Boolean(api?.has?.(button.dataset.productHandle));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? 'Produkt aus der Wishlist entfernen' : 'Größe wählen und Produkt zur Wishlist hinzufügen');
    });
  };

  const enhanceCard = (card) => {
    if (card.closest('[data-scrptd-stl]')) return;
    if (card.dataset.scrptdCardTools === 'true' && card.querySelector('.scrptd-card-media-shell')) return;
    delete card.dataset.scrptdCardTools;
    const originalLink = card.querySelector('a[href*="/products/"]');
    const handle = productHandleFromUrl(originalLink?.href);
    if (!originalLink || !handle || !originalLink.querySelector('img')) return;
    card.dataset.scrptdCardTools = 'true';
    card.classList.add('scrptd-product-card');
    const link = prepareMediaLink(card, originalLink);
    link.classList.add('scrptd-card-media-link');
    if (originalLink !== link) originalLink.classList.add('scrptd-card-info-link');
    const shell = document.createElement('div');
    shell.className = 'scrptd-card-media-shell';
    link.before(shell);
    shell.append(link);
    installActivationGuard(link);
    if (originalLink !== link) installActivationGuard(originalLink);
    card.querySelectorAll('a[href*="/products/"]').forEach(installActivationGuard);

    const quickAdd = document.createElement('button');
    quickAdd.type = 'button';
    quickAdd.className = 'scrptd-card-quick-add';
    quickAdd.setAttribute('aria-label', 'Größe wählen und Produkt in den Warenkorb legen');
    quickAdd.dataset.productHandle = handle;
    shell.append(quickAdd);
    installActivationGuard(quickAdd);

    let quickWishlist = null;
    if (document.body.classList.contains('template-collection') && window.ScrptdWishlist?.enabled !== false) {
      quickWishlist = document.createElement('button');
      quickWishlist.type = 'button';
      quickWishlist.className = 'scrptd-card-quick-wishlist';
      quickWishlist.dataset.productHandle = handle;
      quickWishlist.setAttribute('aria-pressed', 'false');
      quickWishlist.append(wishlistIcon());
      shell.append(quickWishlist);
      installActivationGuard(quickWishlist);
      syncWishlistButtons(card);
    }

    const info = card.querySelector('.product-info-container, .minimal-product-row__info, .collection-display-row__info');
    const title = card.querySelector('.prouduct-title:not(.collection-product-price), .minimal-product-row__title, .collection-display-row__title, .related__product__title');
    const price = card.querySelector('.collection-product-price, .minimal-product-row__price-amount, .collection-display-row__price-amount, .product__price');
    info?.classList.add('scrptd-card-info');
    title?.classList.add('scrptd-card-title');
    price?.classList.add('scrptd-card-price');
    if (info && card.dataset.cardTagLabel && !info.querySelector('.scrptd-card-tag-label')) {
      const tagLabel = document.createElement('span');
      tagLabel.className = 'scrptd-card-tag-label';
      tagLabel.textContent = card.dataset.cardTagLabel;
      info.append(tagLabel);
    }

    quickAdd.addEventListener('click', async () => {
      quickAdd.setAttribute('aria-busy', 'true');
      try {
        const product = await loadProduct(handle);
        renderCarousel(link, product);
        openProductPicker(product, quickAdd);
      } catch (_) {
        window.location.assign(productUrlFromHandle(handle));
      } finally {
        quickAdd.removeAttribute('aria-busy');
      }
    });

    quickWishlist?.addEventListener('click', async () => {
      const api = window.ScrptdWishlist;
      if (api?.has?.(handle)) {
        api.removeProduct?.(handle);
        return;
      }
      quickWishlist.setAttribute('aria-busy', 'true');
      try {
        const product = await loadProduct(handle);
        renderCarousel(link, product);
        openProductPicker(product, quickWishlist, 'wishlist');
      } catch (_) {
        window.location.assign(productUrlFromHandle(handle));
      } finally {
        quickWishlist.removeAttribute('aria-busy');
      }
    });

    const preload = () => queuePreload(card, () => loadProduct(handle).then((product) => renderCarousel(link, product)));
    card.scrptdPreload = preload;
    if ('IntersectionObserver' in window) {
      if (!cardObserver) {
        cardObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            cardObserver.unobserve(entry.target);
            entry.target.scrptdPreload?.();
            delete entry.target.scrptdPreload;
          });
        }, { rootMargin: '80px 0px' });
      }
      cardObserver.observe(card);
    } else preload();
  };

  const enhanceRondellCard = (card) => {
    if (!card || card.dataset.scrptdRondellTools === 'true') return;
    if (card.getAttribute('aria-hidden') === 'true' || card.closest('[data-rondell-clone="true"]')) return;
    const link = card.querySelector('.scrptd-rondell__media-link[href*="/products/"]');
    const handle = card.dataset.productHandle || productHandleFromUrl(link?.href);
    if (!handle) return;
    card.dataset.scrptdRondellTools = 'true';
    card.querySelectorAll('a[href*="/products/"]').forEach(installActivationGuard);

    const quickAdd = card.querySelector('.scrptd-rondell__quick-add');
    if (quickAdd) {
      installActivationGuard(quickAdd);
      quickAdd.addEventListener('click', async () => {
        quickAdd.setAttribute('aria-busy', 'true');
        try {
          const product = await loadProduct(handle);
          openProductPicker(product, quickAdd);
        } catch (_) {
          if (!window.Shopify?.designMode) window.location.assign(productUrlFromHandle(handle));
        } finally {
          quickAdd.removeAttribute('aria-busy');
        }
      });
    }

    const quickWishlist = card.querySelector('.scrptd-rondell__quick-wishlist');
    if (!quickWishlist) return;
    if (window.ScrptdWishlist?.enabled === false) {
      quickWishlist.hidden = true;
      return;
    }
    installActivationGuard(quickWishlist);
    syncWishlistButtons(card);
    quickWishlist.addEventListener('click', async () => {
      const api = window.ScrptdWishlist;
      if (api?.has?.(handle)) {
        api.removeProduct?.(handle);
        return;
      }
      quickWishlist.setAttribute('aria-busy', 'true');
      try {
        const product = await loadProduct(handle);
        openProductPicker(product, quickWishlist, 'wishlist');
      } catch (_) {
        if (!window.Shopify?.designMode) window.location.assign(productUrlFromHandle(handle));
      } finally {
        quickWishlist.removeAttribute('aria-busy');
      }
    });
  };

  const init = (scope = document) => {
    if (scope.nodeType === 1 && scope.matches?.(CARD_SELECTOR)) enhanceCard(scope);
    scope.querySelectorAll?.(CARD_SELECTOR).forEach(enhanceCard);
    if (scope.nodeType === 1 && scope.matches?.(RONDELL_SELECTOR)) enhanceRondellCard(scope);
    scope.querySelectorAll?.(RONDELL_SELECTOR).forEach(enhanceRondellCard);
  };
  window.ScrptdProductCardTools = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('collection:sorted', () => init());
  document.addEventListener('scrptd:wishlist-change', () => syncWishlistButtons());
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.closest?.('.scrptd-card-carousel, .scrptd-quick-add-modal, #cart-drawer')) return;
        if (node.matches?.(`${CARD_SELECTOR},${RONDELL_SELECTOR}`) || node.querySelector?.(`${CARD_SELECTOR},${RONDELL_SELECTOR}`)) pendingMutationRoots.add(node);
      });
    });
    if (!pendingMutationRoots.size) return;
    cancelAnimationFrame(mutationFrame);
    mutationFrame = requestAnimationFrame(() => {
      const roots = Array.from(pendingMutationRoots);
      pendingMutationRoots.clear();
      roots.forEach(init);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
